<!--
/**
 * peacohrm is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 peacohrm Inc., http://www.peacohrm.com
 *
 * peacohrm is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * peacohrm is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <oxd-table-filter filter-title="My Advance Request List">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="3" class="peacohrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
                v-model="filters.referenceId"
                label="Reference Id"
                placeholder="Reference Id"
            />
          </oxd-grid-item>
          <oxd-grid-item>
              <oxd-input-field
                v-model="filters.advanceAmount"
                label="Advance Amount"
                placeholder="Advance Amount"
              />
            </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.requestedDate"
              label="Requesting Date "
              :rules="rules.date"
              :years="yearsArray"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          @click="onClickReset"
        />
        <oxd-button
          class="peacohrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="peacohrm-paper-container">
    <div class="peacohrm-header-container">
      <oxd-button
        label="Request Advance"
        icon-name="plus"
        display-type="secondary"
        @click="onClickAdd"
      />
    </div>
    <table-header :total="total" :loading="isLoading" />
    <div class="peacohrm-container">
      <oxd-card-table
        v-model:order="sortDefinition"
        :items="items.data"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="peacohrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>
  <delete-confirmation ref="deleteDialog"></delete-confirmation>
</template>

<script>
import {ref, computed} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import useSort from '@ohrm/core/util/composable/useSort';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
// import ReferenceIdAutocomplete from '@/peacohrmClaimPlugin/components/ReferenceIdAutocomplete.vue';
// import ClaimEventDropdown from '@/peacohrmClaimPlugin/components/ClaimEventDropdown.vue';
// import StatusDropdown from '@/peacohrmClaimPlugin/components/StatusDropdown.vue';
// import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import DeleteConfirmationDialog from '../../../core/components/dialogs/DeleteConfirmationDialog.vue';

import useLocale from '@/core/util/composable/useLocale';
import {formatDate, parseDate} from '@ohrm/core/util/helper/datefns';
import {
  validDateFormat,
  endDateShouldBeAfterStartDate,
} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';

const defaultFilters = {
  referenceId: '',
  advanceAmount: null,
  requestedDate: null,
};

const defaultSortOrder = {
  'employeeAdvance.referenceId': 'DESC',
  'employeeAdvance.advanceAmount': 'ASC',
  'employeeAdvance.requestedDate': 'ASC',
};

export default {
  components: {
    // 'reference-id-autocomplete': ReferenceIdAutocomplete,
    'delete-confirmation': DeleteConfirmationDialog,
    // 'reimbursement-event-dropdown': ClaimEventDropdown,
    // 'status-dropdown': StatusDropdown,
  },
  setup() {
    const filters = ref({...defaultFilters});
    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });
    const {locale} = useLocale();
    const {jsDateFormat} = useDateFormat();

    const serializedFilters = computed(() => {
      return {
        referenceId:
          typeof filters.value.referenceId === 'object' &&
          filters.value.referenceId
            ? filters.value.referenceId.id
            : typeof filters.value.referenceId === 'string'
            ? filters.value.referenceId
            : null,
        advanceAmount: filters.value.advanceAmount ? filters.value.advanceAmount?.id : null,
        requestedDate: filters.value.requestedDate,
        // sortField:
        //   sortField.value === 'claimRequest.claimEvent.name'
        //     ? 'claimEvent.name'
        //     : sortField.value,
        // sortOrder: sortOrder.value,
      };
    });

    const claimRequestDataNormalizer = (data) => {
      return data.map((item) => {
        let formattedDate = "";
        if(item.requestedDate !== null && item.requestedDate !== ""){
          if(item.requestedDate.date !== null && item.requestedDate.date !== ""){
            formattedDate = item.requestedDate.date.split(' ')[0];
          }
        }
        return {
          id: item.id,
          referenceId: item.referenceId,
          employeeName: item.employeeName,
          currency: item.currency,
          purpose: item.purpose,
          advanceAmount: item.advanceAmount,
          status:
            item.status.charAt(0).toUpperCase() +
            item.status.slice(1).toLowerCase(),
          requestedDate: formattedDate,
          amount: Number(item.amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        };
      });
    };

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/reimbursement/my_advance_requests',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer: claimRequestDataNormalizer,
      query: serializedFilters,
    });
    onSort(execQuery);
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      response,
      filters,
      sortDefinition,
      useDateFormat,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'referenceId',
          title: this.$t('claim.reference_id'),
          slot: 'title',
          // sortField: 'employeeAdvance.referenceId',
          style: {flex: 3},
        },
        {
          name: 'advanceAmount',
          title: 'Advance Amount',
          slot: 'title',
          cellType: 'oxd-table-cell-truncate',
          // sortField: 'employeeAdvance.advanceAmount',
          style: {flex: 3},
        },
        {
          name: 'currency',
          title: this.$t('general.currency'),
          slot: 'title',
          style: {flex: 3},
        },
        {
          name: 'requestedDate',
          title: 'Requesting Date',
          slot: 'title',
          // sortField: 'employeeAdvance.requestedDate',
          style: {flex: 3},
        },
        {
          name: 'status',
          title: this.$t('general.status'),
          //sortField: 'claimRequest.status',
          style: {flex: 2},
        },
        {
          name: 'actions',
          slot: 'right',
          title: this.$t('general.actions'),
          style: {flex: 3},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
        },
      ],
      rules: {
        date: [validDateFormat(this.userDateFormat)],
        toDate: [
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.filters.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
      },
    };
  },

  methods: {
    onClickDelete(item) {
      if (this.$refs.deleteDialog) {
        this.$refs.deleteDialog.showDialog().then((confirmation) => {
          if (confirmation === 'ok') {
            this.deleteData([item.id]);
          }
        });
      } else {
        console.error('$refs.deleteDialog is undefined');
      }
    },

    onClickEdit(item) {
      navigate('/reimbursement/my_advance/{id}', {id: item.id});
    },
    async deleteData(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
            .deleteAll({
              ids: items,
            })
            .then(() => {
              return this.$toast.deleteSuccess();
            })
            .then(() => {
              this.isLoading = false;
              this.resetDataTable();
            });
      }
    },

    cellRenderer(...[, , , row]) {
      console.log("this row");

      const cellConfig = {};

      if (row.status === 'Pending approval') {
        cellConfig.view = {
          onClick: () => this.onClickView(row),
          props: { name: 'eye-fill' },
        };
        cellConfig.edit = {
          onClick: () => this.onClickEdit(row),
          props: { name: 'pencil-fill' },
        };
        cellConfig.delete = {
          onClick: () => this.onClickDelete(row),
          component: 'oxd-icon-button',
          props: { name: 'trash' },
        };
      } else {
        cellConfig.view = {
          onClick: () => this.onClickView(row),
          props: { name: 'eye-fill' },
        };
      }

      return {
        props: {
          header: { cellConfig },
        },
      };
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    filterItems() {
      // Filter items locally based on the filters
      console.log("Filtering data...");
      console.log("Filters:", this.filters);
      console.log("Original data:", this.response.data);

      this.items.data = this.response.data.filter(item => {
        let passFilter = true;

        if (this.filters.referenceId && item.referenceId !== this.filters.referenceId) {
          console.log(`Item with referenceId ${item.referenceId} did not pass filter`);
          passFilter = false;
        }

        if (this.filters.advanceAmount !== null && parseFloat(item.advanceAmount) !== parseFloat(this.filters.advanceAmount)) {
          console.log(`Item with advanceAmount ${item.advanceAmount} did not pass filter`);
          passFilter = false;
        }

        if (this.filters.requestedDate && item.requestedDate !== this.filters.requestedDate) {
          console.log(`Item with requestedDate ${item.requestedDate} did not pass filter`);
          passFilter = false;
        }

        return passFilter;
      });

      this.total = this.items.data.length;
      console.log("Filtered data:", this.items.data);
      console.log("Filtered data count:", this.filteredItemCount);
    },
    onClickReset() {
      this.filters = {...defaultFilters};
      this.filters.referenceId = null; // Clear referenceId field
      this.filterItems();
      this.execQuery();
    },
    onClickAdd() {
      navigate('/reimbursement/employee_advance_request');
    },
    onClickView(item) {
      navigate('/reimbursement/view_employee_advance_request/id/{id}', {id: item.id});
    },
  },
};
</script>
